// src/lib/heatmapStore.ts
// Public-only readers with graceful fallbacks.
// Reads user density from heatmap_public and LGS from lgs_public (by geohash window).
// If lgs_public is empty, falls back to OSM (ephemeral).

import { create } from "zustand";
import { geohashQueryBounds, distanceBetween } from "geofire-common";
import { collection, getDocs, query, orderBy, startAt, endAt, limit, where } from "firebase/firestore";
import { db } from "@/lib/firebase"; // adjust if firebase export differs
import { tryWpnBackfill } from "@/lib/wpnClient";

export type HeatPoint = [number, number, number]; // [lat,lng,weight]

// Unified LGS point interface
interface LgsPoint {
  id: string;
  name: string;
  address?: string;
  website?: string;
  phone?: string;
  lat: number;
  lng: number;
  wpnId?: string;
  g?: string;
  source?: string;
}

interface HeatmapState {
  heatPoints: HeatPoint[];
  lgsPoints: LgsPoint[];
  heatLoading: boolean;
  lgsLoading: boolean;
  heatError: string | null;
  lgsError: string | null;
  debug: string[];
  usersLoaded: boolean;
  usersLoading: boolean;
  selectedLgsId: string | null;
  lgsFilterRadiusM: number;
  lgsLayerEnabled: boolean;
  lgsFetching: boolean;
  _lgsLastCenter?: { lat: number; lng: number };
  _lgsLastFetchTs?: number;
  _lgsSyncInFlight?: boolean;

  // Actions / helpers
  clearHeat: () => void;
  setSelectedLgs: (id: string | null) => void;
  setLgsFilterRadius: (m: number) => void;
  getFilteredHeat: () => HeatPoint[];
  loadAllUserHeat: () => Promise<void>;
  loadWidgetLgs?: (center: [number, number]) => Promise<void>;
  loadViewportLgs?: (bbox: [[number, number], [number, number]]) => Promise<void>;
  fetchNearbyLgs: (center: { lat: number; lng: number; force?: boolean }) => Promise<void>;
  toggleLgsLayer: () => void;
  refreshLgs: () => Promise<void>;
  _setLgsPoints?: (p: LgsPoint[]) => void;
  _setLgsError?: (e: string | null) => void;
  _setLgsSyncInFlight: (v: boolean) => void;
};

const USER_HEAT_COLLECTION = "heatmap_public";
const AGG_HEAT_COLLECTION = "heatmap_cells"; // legacy fallback
const LGS_COLLECTION = "lgs_public"; // centralize collection name
const GEOHASH_FIELD = "g";

const WIDGET_LGS_RADIUS_M = 16093; // 10 miles
const VIEWPORT_FETCH_LIMIT = 250;

function sameHeatPoints(a: HeatPoint[], b: HeatPoint[]) {
  if (a === b || a.length !== b.length) return a === b;
  for (let i = 0; i < a.length; i++) {
    const [alat, alng, aw] = a[i];
    const [blat, blng, bw] = b[i];
    if (alat !== blat || alng !== blng || aw !== bw) return false;
  }
  return true;
}
function sameLgsPoints(a: LgsPoint[], b: LgsPoint[]) {
  if (a === b || a.length !== b.length) return a === b;
  for (let i = 0; i < a.length; i++) {
    const A = a[i],
      B = b[i];
    if (A.id !== B.id || A.lat !== B.lat || A.lng !== B.lng) return false;
  }
  return true;
}
const km = (a: [number, number], b: [number, number]) => distanceBetween(a, b);

// Deterministic obfuscation (stable jitter) so user points are not exact.
// Keeps visual density pattern while masking precise location (~ up to ~1km).
const ENABLE_JITTER = import.meta.env.VITE_HEAT_JITTER !== "0";

function stableJitter(id: string, lat: number, lng: number): [number, number] {
  if (!ENABLE_JITTER) return [lat, lng];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 131 + id.charCodeAt(i)) >>> 0;
  const r1 = ((h & 0xffff) / 0xffff) * 2 - 1;
  const r2 = (((h >>> 16) & 0xffff) / 0xffff) * 2 - 1;
  const scaleLat = 0.009 + Math.abs(r1) * 0.003;        // ~0.009–0.012 deg
  const scaleLng = 0.009 + Math.abs(r2) * 0.003;
  const jLat = lat + r1 * scaleLat;
  const jLng = lng + r2 * scaleLng * Math.cos((lat * Math.PI) / 180);
  return [jLat, jLng];
}

let heatLoadPromise: Promise<void> | null = null;

export const useHeatmapStore = create<HeatmapState>((set, get) => ({
  heatPoints: [],
  lgsPoints: [],
  heatLoading: false,
  lgsLoading: false,
  heatError: null,
  lgsError: null,
  debug: [],
  usersLoaded: false,
  usersLoading: false,
  selectedLgsId: null,
  lgsFilterRadiusM: 16093,
  lgsLayerEnabled: true,
  lgsFetching: false,
  _lgsSyncInFlight: false, // added initial value

  _setLgsPoints: (pts) => set({ lgsPoints: pts }),
  _setLgsError: (err) => set({ lgsError: err }),
  _setLgsSyncInFlight: (v) => set({ _lgsSyncInFlight: v }),

  clearHeat: () => set({ heatPoints: [], heatError: null, heatLoading: false }),

  setSelectedLgs: (id) => set({ selectedLgsId: id }),
  setLgsFilterRadius: (m) =>
    set({ lgsFilterRadiusM: Math.max(1000, Math.min(80467, m)) }), // 1km–50mi

  getFilteredHeat: () => {
    const { heatPoints, selectedLgsId, lgsFilterRadiusM, lgsPoints } = get();
    if (!selectedLgsId) return heatPoints;
    const store = lgsPoints.find((l) => l.id === selectedLgsId);
    if (!store) return heatPoints;
    const r2 = lgsFilterRadiusM * lgsFilterRadiusM;
    // Approx distance using naive lat/lng degree -> meters (small radius)
    const mPerDegLat = 111320;
    const mPerDegLng = (lat: number) => 111320 * Math.cos((lat * Math.PI) / 180);
    return heatPoints.filter(([lat, lng]) => {
      const dLat = (lat - store.lat) * mPerDegLat;
      const dLng = (lng - store.lng) * mPerDegLng(store.lat);
      return dLat * dLat + dLng * dLng <= r2;
    });
  },

  loadAllUserHeat: async () => {
    // Reuse in‑flight promise
    if (heatLoadPromise) return heatLoadPromise;

    // If already loaded, no-op
    if (get().usersLoaded) return;

    get().setHeatLoading?.(true);
    heatLoadPromise = (async () => {
      try {
        let finalPoints: HeatPoint[] = [];
        let usedCollection = USER_HEAT_COLLECTION;

        const snap = await getDocs(collection(db, USER_HEAT_COLLECTION));
        let rawCount = 0;
        snap.forEach((d) => {
          rawCount++;
          const v: any = d.data();
          const lat = Number(
            v?.lat ??
              v?.location?.lat ??
              v?.geo?.lat ??
              v?.loc?.lat ??
              v?.profile?.location?.lat
          );
          const lng = Number(
            v?.lng ??
              v?.location?.lng ??
              v?.geo?.lng ??
              v?.loc?.lng ??
              v?.profile?.location?.lng
          );
          if (
            Number.isFinite(lat) &&
            Number.isFinite(lng) &&
            Math.abs(lat) <= 90 &&
            Math.abs(lng) <= 180
          ) {
            const intensityRaw =
              (typeof v?.intensity === "number" && v.intensity) ||
              (typeof v?.w === "number" && v.w) ||
              Number(v?.intensity) ||
              Number(v?.w) ||
              0.6;
            const w = Math.max(0.05, Math.min(1, intensityRaw));
            const [jLat, jLng] = stableJitter(d.id, lat, lng);
            finalPoints.push([jLat, jLng, w]);
          }
        });

        if (import.meta.env.DEV) {
          console.log(
            "[heat] raw doc count",
            rawCount,
            "parsed",
            finalPoints.length
          );
          if (finalPoints.length < rawCount) {
            console.warn(
              "[heat] some docs skipped (missing / invalid lat,lng) – check shapes"
            );
          }
        }

        // Fallback if zero user docs parsed
        if (finalPoints.length === 0) {
          const aggSnap = await getDocs(collection(db, AGG_HEAT_COLLECTION));
          aggSnap.forEach((d) => {
            const v: any = d.data();
            const lat = Number(v?.lat);
            const lng = Number(v?.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              const w = Math.max(
                0.05,
                Math.min(
                  1,
                  Number(v?.w) || Number(v?.intensity) || 0.5
                )
              );
              finalPoints.push([lat, lng, w]);
            }
          });
          if (finalPoints.length > 0) usedCollection = AGG_HEAT_COLLECTION;
        }

        if (import.meta.env.DEV) {
            console.log(
              "[heat] points (obfuscated)",
              finalPoints.length,
              finalPoints.slice(0, 5)
            );
            if (finalPoints.length === 0)
              console.warn("[heat] NO POINTS FOUND in", USER_HEAT_COLLECTION);
        }

        const prev = get().heatPoints;
        if (!sameHeatPoints(prev, finalPoints)) {
          set({
            heatPoints: finalPoints,
            usersLoaded: true,
            usersLoading: false,
            heatLoading: false,
            debug: [
              ...get().debug,
              `heat loaded (${finalPoints.length}) from ${usedCollection}`,
            ],
          });
        } else {
          set({
            usersLoaded: true,
            usersLoading: false,
            heatLoading: false,
            debug: [...get().debug, `heat unchanged (${usedCollection})`],
          });
        }
      } catch (e: any) {
        set({
          usersLoading: false,
          heatLoading: false,
          heatError: e?.message || "Heat load failed",
        });
      } finally {
        heatLoadPromise = null;
      }
    })();
    return heatLoadPromise;
  },

  loadWidgetLgs: async ([lat, lng]) => {
    set({ lgsLoading: true, lgsError: null });
    try {
      const bounds = geohashQueryBounds([lat, lng], WIDGET_LGS_RADIUS_M);
      const col = collection(db, LGS_COLLECTION);
      const out: Record<string, LgsPoint> = {};
      await Promise.all(
        bounds.map(async ([start, end]) => {
          const qs = await getDocs(
            query(
              col,
              where(GEOHASH_FIELD, ">=", start),
              where(GEOHASH_FIELD, "<=", end)
            )
          );
          qs.forEach((doc) => {
            const d: any = doc.data();
            if (typeof d.lat !== "number" || typeof d.lng !== "number") return;
            out[doc.id] = {
              id: doc.id,
              lat: d.lat,
              lng: d.lng,
              name: d.name || "Store",
              address: d.address,
              phone: d.phone,
              website: d.website,
            };
          });
        })
      );

      if (import.meta.env.DEV) {
        console.debug("[lgs] geohash candidate count =", Object.keys(out).length);
      }

      // Ensure final fallback full scan still happens if empty:
      if (Object.keys(out).length === 0) {
        const fullSnap = await getDocs(collection(db, LGS_COLLECTION));
        fullSnap.forEach((doc) => {
          const d: any = doc.data();
          if (typeof d.lat === "number" && typeof d.lng === "number") {
            out[doc.id] = {
              id: doc.id,
              lat: d.lat,
              lng: d.lng,
              name: d.name || "Store",
              address: d.address,
              phone: d.phone,
              website: d.website,
            };
          }
        });
        if (import.meta.env.DEV)
          console.debug("[lgs] fallback full scan count =", Object.keys(out).length);
      }

      // WPN backfill attempt if empty
      if (Object.keys(out).length === 0) {
        if (import.meta.env.DEV) console.debug("[lgs] Firestore miss (widget)");
        const wpn = await tryWpnBackfill(lat, lng, WIDGET_LGS_RADIUS_M);
        if (wpn) {
          if (import.meta.env.DEV) console.debug("[lgs] re-query after WPN");
          // re-query once
          const qs2 = await getDocs(col);
          qs2.forEach((doc) => {
            const d: any = doc.data();
            if (typeof d.lat !== "number" || typeof d.lng !== "number") return;
            out[doc.id] = {
              id: doc.id,
              lat: d.lat,
              lng: d.lng,
              name: d.name || "Store",
              address: d.address,
              phone: d.phone,
              website: d.website,
            };
          });
        }
      }

      // existing fallback full scan if still empty (leave your earlier fallback logic if present)
      if (Object.keys(out).length === 0) {
        if (import.meta.env.DEV) console.warn("[lgs] still empty after WPN attempt");
      }

      const list = Object.values(out);
      const prev = get().lgsPoints;
      if (!sameLgsPoints(prev, list)) {
        set({ lgsPoints: list, lgsLoading: false });
      } else {
        set({ lgsLoading: false });
      }
    } catch (e: any) {
      set({
        lgsLoading: false,
        lgsError: e?.message || "LGS load failed",
      });
    }
  },

  loadViewportLgs: async ([[swLat, swLng], [neLat, neLng]]) => {
    set({ lgsLoading: true, lgsError: null });
    try {
      const cLat = (swLat + neLat) / 2;
      const cLng = (swLng + neLng) / 2;
      const approxRadiusM = Math.max(
        distanceBetween([swLat, swLng], [neLat, neLng]) * 500,
        10000
      );
      const col = collection(db, LGS_COLLECTION);
      const bounds = geohashQueryBounds([cLat, cLng], approxRadiusM);
      const out: Record<string, LgsPoint> = {};
      await Promise.all(
        bounds.map(async ([start, end]) => {
          const qs = await getDocs(
            query(
              col,
              where(GEOHASH_FIELD, ">=", start),
              where(GEOHASH_FIELD, "<=", end),
              limit(VIEWPORT_FETCH_LIMIT)
            )
          );
            qs.forEach((doc) => {
            const d: any = doc.data();
            if (typeof d.lat !== "number" || typeof d.lng !== "number") return;
            if (
              d.lat >= swLat &&
              d.lat <= neLat &&
              d.lng >= swLng &&
              d.lng <= neLng
            ) {
              out[doc.id] = {
                id: doc.id,
                lat: d.lat,
                lng: d.lng,
                name: d.name || "Store",
                address: d.address,
                phone: d.phone,
                website: d.website,
              };
            }
          });
        })
      );

      if (Object.keys(out).length === 0) {
        if (import.meta.env.DEV) console.debug("[lgs] viewport miss");
        const wpn = await tryWpnBackfill(cLat, cLng, approxRadiusM);
        if (wpn) {
          const qs2 = await getDocs(col);
          qs2.forEach((doc) => {
            const d: any = doc.data();
            if (typeof d.lat !== "number" || typeof d.lng !== "number") return;
            if (
              d.lat >= swLat &&
              d.lat <= neLat &&
              d.lng >= swLng &&
              d.lng <= neLng
            ) {
              out[doc.id] = {
                id: doc.id,
                lat: d.lat,
                lng: d.lng,
                name: d.name || "Store",
                address: d.address,
                phone: d.phone,
                website: d.website,
              };
            }
          });
        }
      }

      const list = Object.values(out);
      const prev = get().lgsPoints;
      if (!sameLgsPoints(prev, list)) {
        set({ lgsPoints: list, lgsLoading: false });
      } else {
        set({ lgsLoading: false });
      }
    } catch (e: any) {
      set({
        lgsLoading: false,
        lgsError: e?.message || "LGS viewport load failed",
      });
    }
  },

  fetchNearbyLgs: async ({ lat, lng, force = false }) => {
    const state = get();
    const now = Date.now();
    // Distance threshold 1 mile ≈ 1.60934 km
    if (!force && state._lgsLastCenter) {
      const km = distanceBetween([lat, lng], [state._lgsLastCenter.lat, state._lgsLastCenter.lng]);
      if (km < 1.60934 && state._lgsLastFetchTs && now - state._lgsLastFetchTs < 1000 * 60 * 15) {
        return;
      }
    }
    if (state.lgsFetching) return;
    set({ lgsFetching: true, lgsError: null });

    try {
      const radiusMiles = 10;
      const radiusKm = radiusMiles * 1.60934;
      const bounds = geohashQueryBounds([lat, lng], radiusKm * 1000);
      const seen = new Map<string, LgsPoint>();
      // run sequentially to stay under concurrent limits (or could Promise.all)
      for (const b of bounds) {
        const qRef = query(
          collection(db, LGS_COLLECTION),
          orderBy("g"),
          startAt(b[0]),
          endAt(b[1]),
          limit(100)
        );
        const snap = await getDocs(qRef);
        snap.forEach((d) => {
          const data = d.data() as any;
            if (typeof data.lat !== "number" || typeof data.lng !== "number") return;
            const distKm = distanceBetween([lat, lng], [data.lat, data.lng]);
            if (distKm <= radiusKm) {
              seen.set(d.id, {
                id: d.id,
                name: data.name,
                address: data.address,
                website: data.website || data.site || data.url,
                lat: data.lat,
                lng: data.lng,
                wpnId: data.wpnId,
                source: data.source,
                g: data.g,
              });
            }
        });
      }
      // merge with existing (preserve any extended props)
      const existing = get().lgsPoints.reduce((acc, p) => {
        acc.set(p.id, p);
        return acc;
      }, new Map<string, LgsPoint>());
      for (const [id, p] of seen) {
        existing.set(id, p);
      }
      set({
        lgsPoints: Array.from(existing.values()),
        _lgsLastCenter: { lat, lng },
        _lgsLastFetchTs: now,
      });
    } catch (e: any) {
      set({ lgsError: e?.message || "Failed to load stores" });
    } finally {
      set({ lgsFetching: false });
    }
  },
  toggleLgsLayer: () =>
    set((s: HeatmapState) => ({ lgsLayerEnabled: !s.lgsLayerEnabled })),
  refreshLgs: async () => {
    const c = get()._lgsLastCenter;
    if (c) {
      await get().fetchNearbyLgs({ ...c, force: true });
    }
  },
}));

// Selectors
export const useHeatmapPoints = () => useHeatmapStore((s) => s.heatPoints);
export const useLgsPoints = () => useHeatmapStore((s) => s.lgsPoints);
export const useLgsLayerEnabled = () => useHeatmapStore((s) => s.lgsLayerEnabled);
export const useHeatLoading = () => useHeatmapStore((s) => s.heatLoading);
export const useLgsLoading = () => useHeatmapStore((s) => s.lgsLoading);
export const useHeatError = () => useHeatmapStore((s) => s.heatError);
export const useLgsError = () => useHeatmapStore((s) => s.lgsError);
export const useHeatCounts = () =>
  useHeatmapStore((s) => ({ cells: s.heatPoints.length, lgs: s.lgsPoints.length }));
export const useSelectedLgsId = () =>
  useHeatmapStore((s) => s.selectedLgsId);
export const useSetSelectedLgs = () =>
  useHeatmapStore((s) => s.setSelectedLgs);
export const useFilteredHeatPoints = () =>
  useHeatmapStore((s) => s.getFilteredHeat());

